import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Link,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { commissionApi } from '../../services/api';

export default function CookieDetailPanel({ itemId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cookieData, setCookieData] = useState(null);

  useEffect(() => {
    const fetchCookieData = async () => {
      if (!itemId) return;

      try {
        setLoading(true);
        setError(null);
        const result = await commissionApi.getCookieData(itemId);
        if (result.success) {
          setCookieData(result.cookie);
        } else {
          setError(result.error || 'Failed to fetch cookie data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCookieData();
  }, [itemId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!cookieData) {
    return <Alert severity="info">No cookie data available</Alert>;
  }

  const { clicked_article } = cookieData;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Cookie Information
      </Typography>

      {/* Cookie Usage Status */}
      <Box sx={{ mb: 2 }}>
        {cookieData.already_approved ? (
          <Alert severity="warning" icon={<WarningIcon />}>
            <Typography variant="body2">
              Cookie already used for another approval
            </Typography>
            {cookieData.existing_approval && (
              <Typography variant="caption" display="block">
                Order: {cookieData.existing_approval.order_number} |{' '}
                Status: {cookieData.existing_approval.status} |{' '}
                Approved: {cookieData.existing_approval.approved_at
                  ? format(new Date(cookieData.existing_approval.approved_at), 'MMM d, yyyy')
                  : '-'}
              </Typography>
            )}
          </Alert>
        ) : (
          <Alert severity="success" icon={<CheckIcon />}>
            Cookie has not been used for any other approval
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Cookie Details */}
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Cookie ID:
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {cookieData.cookie_id?.slice(0, 8)}...
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Reference Retailer:
          </Typography>
          <Typography variant="body2">{cookieData.reference_retailer || '-'}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Match Retailer:
          </Typography>
          <Typography variant="body2">{cookieData.match_retailer || '-'}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Clicked At:
          </Typography>
          <Typography variant="body2">
            {cookieData.clicked_at
              ? format(new Date(cookieData.clicked_at), 'MMM d, yyyy HH:mm')
              : cookieData.click_timestamp
              ? format(new Date(cookieData.click_timestamp), 'MMM d, yyyy HH:mm')
              : '-'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Order Placed:
          </Typography>
          <Typography variant="body2">
            {cookieData.order_timestamp
              ? format(new Date(cookieData.order_timestamp), 'MMM d, yyyy HH:mm')
              : '-'}
          </Typography>
        </Box>
      </Stack>

      {/* Clicked Article */}
      {clicked_article && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Clicked Product
          </Typography>
          <Stack spacing={1}>
            {clicked_article.name && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Name:
                </Typography>
                <Typography variant="body2">{clicked_article.name}</Typography>
              </Box>
            )}

            {clicked_article.brand && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Brand:
                </Typography>
                <Typography variant="body2">{clicked_article.brand}</Typography>
              </Box>
            )}

            {clicked_article.color && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Color:
                </Typography>
                <Typography variant="body2">{clicked_article.color}</Typography>
              </Box>
            )}

            {clicked_article.size && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Size:
                </Typography>
                <Typography variant="body2">{clicked_article.size}</Typography>
              </Box>
            )}

            {clicked_article.price != null && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Price:
                </Typography>
                <Typography variant="body2">{clicked_article.price.toFixed(2)}</Typography>
              </Box>
            )}

            {clicked_article.url && (
              <Box>
                <Link
                  href={clicked_article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  View Product <OpenIcon fontSize="small" />
                </Link>
              </Box>
            )}
          </Stack>
        </>
      )}
    </Paper>
  );
}
